import { ReactNode, useEffect } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Box, CircularProgress } from '@mui/material'
import { useAuth } from '../contexts/AuthContext'
import { isCliAuthPath, needsTermsAcceptance, termsRedirectPath } from '../utils/publicRoutes'
import { stashCliAuthReturn } from '../utils/cliAuthReturn'
import { reportCliLoginVisit } from '../utils/cliLoginVisit'

interface ProtectedRouteProps {
  children: ReactNode
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const needsTos = needsTermsAcceptance(user, location.pathname)

  useEffect(() => {
    if (!isCliAuthPath(location.pathname)) return
    reportCliLoginVisit(`${location.pathname}${location.search}`)
  }, [location.pathname, location.search])

  useEffect(() => {
    if (isLoading || !isAuthenticated || !needsTos) return
    navigate(termsRedirectPath(location.search), { replace: true })
  }, [isLoading, isAuthenticated, needsTos, location.search, navigate])

  if (isLoading || needsTos) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
      }}>
        <CircularProgress size={60} />
      </Box>
    )
  }

  if (!isAuthenticated) {
    stashCliAuthReturn(`${location.pathname}${location.search}`)
    // Never bounce auth tokens back onto /login — AuthContext consumes them once.
    const params = new URLSearchParams(location.search)
    params.delete('impersonate')
    params.delete('token')
    const qs = params.toString()
    const loginTarget = qs ? `/login?${qs}` : '/login'
    return <Navigate to={loginTarget} replace state={{ from: location }} />
  }

  return <>{children}</>
}

export default ProtectedRoute
